import { API_URL, fetchWithErrorHandling } from "../utils/api";
import { AuthResponse } from "./auth";

export type BridgeProvider = "google" | "apple" | "vk";

export function isValidNonce(nonce: string): boolean {
  return typeof nonce === "string" && /^[a-f0-9]{64}$/.test(nonce);
}

export function generateNonce(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export function buildBridgeUrl(nonce: string, provider: string): string {
  return `${window.location.origin}/web?tgauth=${nonce}&provider=${provider}`;
}

export function isBridgeMode(): boolean {
  return new URLSearchParams(window.location.search).has("tgauth");
}

export function parseBridgeParams(): { nonce: string; provider: string } {
  const q = new URLSearchParams(window.location.search);
  return { nonce: q.get("tgauth") || "", provider: q.get("provider") || "" };
}

// Browser side: hand the verified session to the server, keyed by the nonce.
export async function completeBridge(nonce: string, session: AuthResponse): Promise<void> {
  await fetchWithErrorHandling(`${API_URL}/auth/bridge/complete`, {
    method: "POST",
    body: JSON.stringify({ nonce, session }),
  });
}

// Mini App side: poll until the browser completes, then store + return the
// session. Returns null on timeout or cancel. Transient poll errors are
// swallowed and retried until the deadline.
export async function pollBridge(
  nonce: string,
  opts: { intervalMs?: number; timeoutMs?: number; isCancelled?: () => boolean } = {}
): Promise<AuthResponse | null> {
  const intervalMs = opts.intervalMs ?? 2000;
  const timeoutMs = opts.timeoutMs ?? 120000;
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (opts.isCancelled?.()) return null;
    try {
      const res = await fetchWithErrorHandling(
        `${API_URL}/auth/bridge/poll?nonce=${encodeURIComponent(nonce)}`
      );
      if (res?.status === "ready" && res.session?.token && res.session?.user) {
        localStorage.setItem("authToken", res.session.token);
        localStorage.setItem("user", JSON.stringify(res.session.user));
        return res.session as AuthResponse;
      }
    } catch {
      /* transient — keep polling until the deadline */
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return null;
}
